import * as React from "react";
import { Modal, Tabs } from "antd";
import { v4 as uuidv4 } from "uuid";
import { getDefaultUnmanagedNodePoolInput } from "../../../draft/utils/utils";
import { PlusOutlined } from "@ant-design/icons";
import { GenericNodePool } from "./nodePool";
import { GenericNoNodePool } from "./noNodePool";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { cloneDeep } from "lodash";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { TabLabel } from "../../../../workload/draft/tabLabel";

type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

interface Props {}
export const GenericNodePools: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const nodePools = useCpFormValue("generic.nodePools");
  const activeKey = useCpFormValue(`meta.generic.activeKeyNodePool`) || nodePools[0]?.id || "";

  function onEdit(targetKey: React.MouseEvent | React.KeyboardEvent | string, action: "add" | "remove") {
    if (action === "add") {
      add();
    } else {
      Modal.confirm({
        title: "Remove Node Pool",
        content: `Configuration that was made on this node pool will be lost`,
        onOk() {
          remove(targetKey);
        },
      });
    }
  }

  function add() {
    const newActiveKey = uuidv4();

    const _nodePools = cloneDeep(nodePools);
    _nodePools.push({
      ...getDefaultUnmanagedNodePoolInput(),
      id: newActiveKey,
    });
    formSetValue(id, `generic.nodePools`, _nodePools);

    formSetValue(id, `meta.generic.activeKeyNodePool`, newActiveKey);
  }

  function remove(targetKey: TargetKey) {
    let newActiveKey = activeKey;
    let lastIndex = -1;
    let removeIndex = -1;

    nodePools.forEach((item, i) => {
      if (item.id === targetKey) {
        lastIndex = i - 1;
        removeIndex = i;
      }
    });

    if (removeIndex >= 0) {
      const _nodePools = cloneDeep(nodePools);
      _nodePools.splice(removeIndex, 1);
      formSetValue(id, `generic.nodePools`, _nodePools);
      formTriggerValidation(id);

      if (nodePools.length > 1 && newActiveKey === targetKey) {
        if (lastIndex >= 0) {
          newActiveKey = nodePools[lastIndex].id!;
        } else {
          newActiveKey = nodePools[removeIndex + 1]?.id || nodePools[0]?.id || "";
        }
        formSetValue(id, `meta.generic.activeKeyNodePool`, newActiveKey);
      }
    }
  }

  function onChange(newActiveKey: string) {
    formSetValue(id, `meta.generic.activeKeyNodePool`, newActiveKey);
  }

  return (
    <div className="cpln-form-section cpln-ant-card-container cpln-ant-tabs-container">
      <Tabs
        type={"editable-card"}
        items={nodePools.map((np, i) => {
          const label = np.name || "Node Pool";

          return {
            key: np.id!,
            label: <TabLabel label={label} names={[`generic.nodePools.${i}`]} showDirty />,
            closable: true,
            children: <GenericNodePool index={i} />,
          };
        })}
        onChange={onChange}
        activeKey={activeKey}
        onEdit={onEdit}
        addIcon={
          <div className="flex items-center gap-2 px-4">
            <div>Add</div>
            <PlusOutlined />
          </div>
        }
      />
      {nodePools.length < 1 ? <GenericNoNodePool /> : null}
    </div>
  );
};
